import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { BookOpen, Loader2, Lock } from "lucide-react";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-blue px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-white border-2 border-black rounded-lg shadow-brutal-lg p-8">
        <div className="flex items-center gap-2 mb-6">
          <span className="w-11 h-11 flex items-center justify-center bg-brand-gold border-2 border-black rounded-md">
            <BookOpen className="w-6 h-6 text-brand-blue" strokeWidth={2.5} />
          </span>
          <div>
            <p className="font-display font-black text-xl text-brand-blue leading-none">BooksNmore</p>
            <p className="text-xs text-brand-black/50">Admin panel</p>
          </div>
        </div>
        <h1 className="font-display font-bold text-2xl text-brand-black mb-1">Sign in</h1>
        <p className="text-sm text-brand-black/50 mb-6">Manage your store content and products.</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Username</label>
            <input data-testid="login-username" type="text" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} required className="w-full px-4 py-3 border-2 border-black rounded-md focus:outline-none focus:ring-2 focus:ring-brand-gold" />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Password</label>
            <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full px-4 py-3 border-2 border-black rounded-md focus:outline-none focus:ring-2 focus:ring-brand-gold" />
          </div>
          {error && <p data-testid="login-error" className="text-sm text-red-600 font-medium">{error}</p>}
          <button type="submit" disabled={loading} data-testid="login-submit" className="w-full inline-flex items-center justify-center gap-2 bg-brand-gold text-black font-semibold px-6 py-3 border-2 border-black rounded-md shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 transition-all disabled:opacity-60">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />} Sign in
          </button>
        </form>
      </div>
    </div>
  );
}
